import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Stack,
  Paper,
  Divider,
  CircularProgress,
  Container,
  Link,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from "@mui/material";
import {
  PlayCircleOutline,
  CheckCircle,
  ErrorOutline,
  VideoLibrary,
  Restaurant,
  Close,
  Add
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import useThemeColor from "../../utils/useTheme";
import { submitQuestionQA } from "../../api/userApi";
import {
  convertTextToOnlyLink,
  normalizeUrl
} from "../../utils/getUrlFromText";
import {
  completeProfile,
  fetchCurrentUser
} from "../../features/auth/authSlice";
import { selectCurrentUser } from "../../features/auth/authSelectors";
import PastRequestsTable from "../Extra/PastRequestsTable";
import useSignedUrl from "../../utils/useSignedUrl";

dayjs.extend(relativeTime);

const getVideoEmbedUrl = (url) => {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);

    // YouTube (watch)
    if (
      parsedUrl.hostname.includes("youtube.com") &&
      parsedUrl.searchParams.get("v")
    ) {
      const videoId = parsedUrl.searchParams.get("v");
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // YouTube (shortened youtu.be)
    if (parsedUrl.hostname.includes("youtu.be")) {
      const videoId = parsedUrl.pathname.split("/")[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Vimeo
    if (parsedUrl.hostname.includes("vimeo.com")) {
      const videoId = parsedUrl.pathname.split("/")[1];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    // Dailymotion
    if (parsedUrl.hostname.includes("dailymotion.com")) {
      const videoId = parsedUrl.pathname.split("/video/")[1]?.split("_")[0];
      return `https://www.dailymotion.com/embed/video/${videoId}`;
    }

    // MP4 or direct video file
    const isVideoFile = /\.(mp4|webm|ogg|mov|avi|flv|wmv)$/i.test(
      parsedUrl.pathname
    );
    if (isVideoFile) {
      return url; // return raw URL to use in <video> tag
    }

    // Fallback for unknown URL types
    return url;
  } catch (err) {
    console.error("Invalid video URL:", url);
    return null;
  }
};

// ✅ Memoized Video Player — now quote bubble style
const VideoPlayer = React.memo(({ ecPhoto, heading, title, video, styles }) => {
  const embedUrl = getVideoEmbedUrl(video.url);

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        borderRadius: 1,
        bgcolor: "transparent",
        ...(heading === "Last Week" && {
          mt: 3
        })
      }}
    >
      <CardContent sx={{ p: "0 !important" }}>
        {video.url && (
          <Box
            sx={{
              position: "relative",
              paddingTop: "56.25%",
              bgcolor: "#000",
              borderRadius: 1,
              overflow: "hidden",
              mb: 5
            }}
          >
            {embedUrl ? (
              <iframe
                key={video.url}
                src={embedUrl}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none"
                }}
                allowFullScreen
                title={title}
              />
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column"
                }}
              >
                <VideoLibrary sx={{ fontSize: 48, color: "#999", mb: 2 }} />
                <Typography
                  variant="body2"
                  sx={{ color: styles.color, textAlign: "center", mb: 1 }}
                >
                  Video format not supported for preview
                </Typography>
                <Button
                  variant="outlined"
                  href={normalizeUrl(video.url)}
                  target="_blank"
                  startIcon={<PlayCircleOutline />}
                  sx={{ mt: 1, color: styles.color, borderColor: "#4e4e4e" }}
                  size="small"
                >
                  Open Video
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Quote bubble style announcement */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: { xs: 2, sm: 2 },
            mt: video.url ? 1 : 0,
            width: "100%",
            overflow: "hidden"
          }}
        >
          <Avatar
            src={ecPhoto}
            sx={{
              width: { xs: 40, sm: 56, md: 69 },
              height: { xs: 40, sm: 56, md: 69 },
              mt: 0.5,
              flexShrink: 0
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={(theme) => ({
                position: "relative",
                backgroundColor: styles.lightGreyColor,
                borderRadius: "16px",
                p: { xs: 2, sm: 2.5 },
                color: styles.color,
                fontSize: { xs: 14, sm: 16 }, // 🔑 scales with screen
                fontWeight: 400,
                lineHeight: 1.6,
                whiteSpace: "pre-line",
                wordBreak: "break-word",

                "&::before": {
                  content: '""',
                  position: "absolute",

                  // 🔑 Responsive positioning
                  top: { xs: "1.2em", sm: "1.4em" },

                  // 🔑 Keep tail attached cleanly
                  left: { xs: "-0.6em", sm: "-0.7em" },

                  width: 0,
                  height: 0,

                  // 🔑 Responsive triangle size (scales with text)
                  borderTop: "0.9em solid transparent",
                  borderBottom: "0.9em solid transparent",
                  borderRight: `0.7em solid ${styles.lightGreyColor}`
                }
              })}
            >
              {video?.comments ? convertTextToOnlyLink(video?.comments) : null}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: styles.modLabel,
                display: "block",
                textAlign: "right",
                mt: 0.5
              }}
            >
              {dayjs.utc(video.modifiedAt).format("MMM DD, YYYY")}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

// ✅ Video rendering section extracted
const WeeklyVideos = ({ currentWeekVideo, ecPhoto, lastWeekVideo, styles }) => {
  const formatRange = (dateStr) =>
    `(${dayjs(dateStr).subtract(7, "days").format("MMM DD, YYYY")} - ${dayjs(
      dateStr
    ).format("MMM DD, YYYY")})`;

  // Only show if there's actual content
  if (!currentWeekVideo.url && !currentWeekVideo.comments) {
    return null;
  }

  return (
    <>
      {(currentWeekVideo.url || currentWeekVideo.comments) && (
        <VideoPlayer
          video={currentWeekVideo}
          styles={styles}
          ecPhoto={ecPhoto}
        />
      )}
    </>
  );
};

const UserWeeklyQA = ({
  setOpenSubscriptionModal,
  isViewingUserMeals,
  setIsViewingUserMeals
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const styles = useThemeColor();
  const [question, setQuestion] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [askQuestionOpen, setAskQuestionOpen] = useState(false);
  const ecPhoto = useSignedUrl(
    "EC.jpg",
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const currentWeekVideo = user?.adminQALink?.currentWeek || {};
  const lastWeekVideo = user?.adminQALink?.lastWeek || {};

  const hasAnnouncement = !!(currentWeekVideo.url || currentWeekVideo.comments);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  useEffect(() => {
    if (
      !user?.isFailedPayment &&
      !(user?.isAdmin === 1 || ["Coach", "Company"].includes(user?.userType)) &&
      !user?.isSubscriptionActive &&
      (user?.isTrainingFreeTrialExpired || user?.isDiaryFreeTrialExpired)
    ) {
      setOpenSubscriptionModal(true);
    }
  }, [user]);

  const handleSubmitQuestion = async () => {
    if (isSubmitting || !question.trim()) {
      showSnackbar("Please enter your question", "error");
      return;
    }

    try {
      setIsSubmitting(true);
      await submitQuestionQA({ question: question.trim() });
      await new Promise((res) => setTimeout(res, 1500));
      setQuestion("");
      showSnackbar("Thanks for the question!", "success");
      setAskQuestionOpen(false);
    } catch (err) {
      console.error(err);
      showSnackbar("Error submitting question. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    dispatch(
      completeProfile({
        formData: { checkQALinkReminder: false },
        userId: user?.userId,
        date: dayjs().format("YYYY-MM-DD")
      })
    );
    setTimeout(() => {
      dispatch(fetchCurrentUser(dayjs().format("YYYY-MM-DD")));
    }, 2000);
  }, [dispatch]);
  const userData = useSelector(selectCurrentUser);

  const isSubscribedOrAdmin =
    userData?.isAdmin === 1 ||
    ["Coach", "Company"].includes(userData?.userType) ||
    userData?.isSubscriptionActive ||
    (!userData?.isTrainingFreeTrialExpired &&
      !userData?.isDiaryFreeTrialExpired) ||
    userData?.isFailedPayment;

  return !(
    userData?.isAdmin === 1 || ["Coach", "Company"].includes(userData?.userType)
  ) &&
    !userData?.isSubscriptionActive &&
    (userData?.isTrainingFreeTrialExpired ||
      userData?.isDiaryFreeTrialExpired) &&
    !userData?.isFailedPayment ? (
    <></>
  ) : (
    <Container
      sx={{
        width: { xs: "100%", md: "60%" },
        justifyContent: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        margin: "0"
      }}
    >
      {/* Header with title + Ask Question button */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, bgcolor: "transparent", width: "100%" }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography
            sx={{ fontSize: "30px", color: styles.color, fontWeight: 400 }}
          >
            Nutrition Q&A
          </Typography>
          {!user?.isSubscriptionActive &&
          !user?.isAdmin &&
          ["Gym", "Affiliate", "Individual"].includes(user?.userType) ? null : (
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setAskQuestionOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: 1,
                px: 2,
                py: 0.8,
                bgcolor: styles.primaryColor,
                color: "#fff",
                "&:hover": { opacity: 0.9, bgcolor: styles.primaryColor }
              }}
            >
              Ask Question
            </Button>
          )}
        </Box>
      </Paper>

      {/* Announcement (only shows when content exists) */}
      <WeeklyVideos
        currentWeekVideo={currentWeekVideo}
        lastWeekVideo={lastWeekVideo}
        styles={styles}
        ecPhoto={ecPhoto}
      />

      {/* Divider below announcement (only when announcement present) */}
      {hasAnnouncement && (
        <Divider sx={{ background: styles.notesColor, width: "100%", mt: 2 }} />
      )}

      {/* Past Q&A (moved up, no title) */}
      <PastRequestsTable
        styles={styles}
        isViewingUserMeals={isViewingUserMeals}
        setIsViewingUserMeals={setIsViewingUserMeals}
      />

      {/* Ask Question Dialog */}
      <Dialog
        open={askQuestionOpen}
        onClose={() => setAskQuestionOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: styles.background,
            color: styles.color,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            pb: 0
          }}
        >
          <IconButton
            onClick={() => setAskQuestionOpen(false)}
            sx={{ color: styles.color }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            px: { xs: 3, sm: 4 },
            pb: 4
          }}
        >
          {/* Food icon */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: `2px solid ${styles.primaryColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Restaurant sx={{ fontSize: 28, color: styles.primaryColor }} />
          </Box>

          {/* Info text */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "16px",
              textAlign: "center",
              color: styles.color
            }}
          >
            Nutrition questions are <br /> answered 1x/day M-F AM EST.
          </Typography>

          {/* Tech support link */}
          <Typography
            sx={{
              fontSize: "14px",
              textAlign: "center",
              color: styles.primaryColor
            }}
          >
            <Link
              href="/profile?oc=1"
              sx={{
                color: styles.primaryColor,
                fontWeight: 600,
                "&:hover": {
                  textDecoration: "underline",
                  opacity: 0.8
                }
              }}
            >
              Need tech support? Go here
            </Link>
          </Typography>

          {/* Question input */}
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter your question "
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                color: styles.color,
                whiteSpace: "pre-wrap"
              },
              "& .MuiInputBase-input": {
                whiteSpace: "pre-wrap"
              },
              "& .MuiInputBase-input::placeholder": {
                color: styles.color,
                opacity: 0.7
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#4e4e4e"
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#4e4e4e"
              }
            }}
          />

          {/* Submit button */}
          <Box
            display="flex"
            justifyContent={{ xs: "stretch", sm: "flex-end" }}
            width="100%"
          >
            <Button
              variant="contained"
              size="medium"
              onClick={handleSubmitQuestion}
              disabled={isSubmitting || !question.trim()}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} sx={{ color: styles.color }} />
                ) : null
              }
              sx={{
                py: 1,
                px: 3,
                borderRadius: 1,
                textTransform: "none",
                minWidth: { xs: "100%", sm: 140 },
                bgcolor: styles.primaryColor,
                color: "#fff",
                "&:hover": { bgcolor: "transparent", opacity: 0.8 },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  backgroundColor: "#A7C8E9",
                  color: "#64AEDD"
                }
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", zIndex: 100000000000 }}
          icon={
            snackbar.severity === "success" ? <CheckCircle /> : <ErrorOutline />
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserWeeklyQA;
